import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  HttpBackend,
  HttpClientModule,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HeaderBarComponent } from './protected/header-bar/header-bar.component';
import { NavBarComponent } from './protected/nav-bar/nav-bar.component';
import { DevicesComponent } from './devices/devices.component';
import { DeviceListComponent } from './devices/device-list/device-list.component';
import { DeviceDetailComponent } from './devices/device-detail/device-detail.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { MatSliderModule } from '@angular/material/slider';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatTableModule } from '@angular/material/table';
import {
  MatPaginatorModule,
  MatPaginatorIntl,
} from '@angular/material/paginator';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { LayoutModule } from '@angular/cdk/layout';
import {
  MAT_DIALOG_DEFAULT_OPTIONS,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSortModule } from '@angular/material/sort';
import { ProtectedComponent } from './protected/protected.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { ConfigParamService } from './core/config-param/config-param.service';
import { NoPermissionComponent } from './no-permission/no-permission.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DeviceParametersComponent } from './devices/device-detail/device-parameters/device-parameters.component';
import { HierarchiesComponent } from './hierarchies/hierarchies.component';
import { MatSelectModule } from '@angular/material/select';
import { NgxGraphModule } from '@swimlane/ngx-graph';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { CookieModule } from 'ngx-cookie';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { CustomMatPaginatorIntl } from './shared/config/customMatPaginatorIntl';
import { ErrorInterceptor } from './core/auth/error.interceptor';
import { LoadComponent } from './load/load.component';
import { LoginComponent } from './login/login.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { DeviceAttributesComponent } from './devices/device-detail/device-attributes/device-attributes.component';
import { DeviceOpstateItemsComponent } from './devices/device-detail/device-opstate-items/device-opstate-items.component';
import { DeviceBasicComponent } from './devices/device-detail/device-basic/device-basic.component';
import { DeviceCardComponent } from './devices/device-card/device-card.component';
import { DeviceSensitiveComponent } from './devices/device-detail/device-sensitive/device-sensitive.component';
import { DeviceEventsComponent } from './devices/device-events/device-events.component';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DeviceSalesForceComponent } from './devices/device-detail/device-sales-force/device-sales-force.component';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
  MatRippleModule,
} from '@angular/material/core';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { TypeIconDirective } from './devices/shared/directives/type-icon.directive';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { DefaultDatePipe } from './shared/pipes/default-date.pipe';
import { DatePipe, registerLocaleData } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { AuthInterceptor } from './core/auth/auth.interceptor';
import { CallbackComponent } from './callback/callback.component';
import { MessagesComponent } from './messages/messages.component';
import { MessageCardComponent } from './messages/message-card/message-card.component';
import { SortComponent } from './components/sort/sort.component';
import { MessageDetailComponent } from './messages/message-detail/message-detail.component';
import { MessageDatePipe } from "./shared/pipes/message-date.pipe";
import localeHu from '@angular/common/locales/hu';
import localeEn from '@angular/common/locales/en';
import { MapComponent } from './map/map.component';
import { PdfSelectionDialogComponent } from './pdf/pdf-selection-dialog/pdf-selection-dialog.component';

export const configParamFactory = (configParamService: ConfigParamService) => {
  return () => configParamService.loadConfig();
};

export function httpLoaderFactory(_httpBackend: HttpBackend) {
  return new MultiTranslateHttpLoader(_httpBackend, [
    {
      prefix: './assets/i18n/core/',
      suffix: '.json?cb=' + new Date().setHours(0, 0, 0, 0),
    },
  ]);
}

registerLocaleData(localeHu, 'HUN');
registerLocaleData(localeEn, 'ENG');

@NgModule({
  declarations: [
    AppComponent,
    HeaderBarComponent,
    NavBarComponent,
    DevicesComponent,
    DeviceListComponent,
    DeviceDetailComponent,
    DashboardComponent,
    ProtectedComponent,
    PageNotFoundComponent,
    NoPermissionComponent,
    DeviceParametersComponent,
    HierarchiesComponent,
    LoadComponent,
    LoginComponent,
    DeviceAttributesComponent,
    DeviceOpstateItemsComponent,
    DeviceBasicComponent,
    DeviceCardComponent,
    DeviceSensitiveComponent,
    DeviceSalesForceComponent,
    TypeIconDirective,
    DefaultDatePipe,
    MessageDatePipe,
    CallbackComponent,
    DeviceEventsComponent,
    MessagesComponent,
    MessageCardComponent,
    SortComponent,
    MessageDetailComponent,
    MapComponent,
    PdfSelectionDialogComponent,
  ],
  imports: [
    BrowserModule,
    FormsModule,
    HttpClientModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MatSliderModule,
    MatSidenavModule,
    MatToolbarModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatTableModule,
    MatPaginatorModule,
    MatGridListModule,
    MatCardModule,
    MatMenuModule,
    LayoutModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatSortModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatTabsModule,
    MatButtonToggleModule,
    NgxGraphModule,
    CookieModule.withOptions(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpBackend],
      },
    }),
    MatChipsModule,
    MatExpansionModule,
    MatTooltipModule,
    MatRippleModule,
    ClipboardModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSnackBarModule,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: configParamFactory,
      deps: [ConfigParamService],
      multi: true,
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { panelClass: 'mat-dialog-override' },
    },
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    DatePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'YYYY-MM-DD',
        },
        display: {
          dateInput: 'YYYY-MM-DD',
          monthYearLabel: 'YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'YYYY',
        },
      },
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
